import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {isMac} from '@wandb/weave/common/util/browser';
import React, {FC, memo, useCallback, useEffect, useState} from 'react';
import {Route, useHistory, useParams} from 'react-router-dom';
import TimeAgo from 'react-timeago';
import {Button, Image, Popup} from 'semantic-ui-react';

import {ReactComponent as IconHome} from '../../assets/SVG/icon-home.svg';
import {useDispatch, useSelector} from '../../state/hooks';
import * as ViewerHooks from '../../state/viewer/hooks';
import * as ViewActions from '../../state/views/actions';
import * as WorkspaceActions from '../../state/workspaces/actions';
import {useWorkspaceAction} from '../../state/workspaces/hooks';
import {getWorkspaceType} from '../../state/workspaces/names';
import {WorkspaceTypes} from '../../state/workspaces/types';
import {viewingAs} from '../../util/admin';
import {isOneOf} from '../../util/utility';
import {navigateFromTempViewBar} from './../../services/analytics';
import {URL_STATE_KEYS, urlStateHasKey} from './../../services/url';
import {useTemporaryViewReload} from './useTemporaryViewReload';
import * as S from './ViewBar.styles';
import {ViewMenu} from './ViewMenu';

export const LOGGED_OUT_USER_ID = 'ANONYMOUS';

interface ViewBarProps {
  entityName: string;
  projectName: string;

  workspaceID: string;

  extraActions?: React.ReactNode;
  readOnly?: boolean;
  isRun?: boolean;
}

type AllViewBarProps = ViewBarProps & ReturnType<typeof useViewBarProps>;

interface ReadOnlyBarProps {
  entityName: string;
  projectName: string;
  cantUndo: boolean;
  cantRedo: boolean;
}

const ReadOnlyBarComp: FC<ReadOnlyBarProps> = ({
  entityName,
  projectName,
  cantUndo,
  cantRedo,
}) => {
  useEffect(() => {
    window.analytics?.track('View only workspace bar viewed');
  }, []);

  return (
    <div className="view-bar readonly-view">
      Viewing {entityName}'s project
      {
        <Popup
          wide={true}
          position="top right"
          content={`You have read-only access to ${projectName}, so changes you make to this workspace are not saved.`}
          trigger={
            <span className="help-text underline-dashed">
              {!cantUndo || !cantRedo ? 'Changes not saved' : 'Read only'}
            </span>
          }
        />
      }
    </div>
  );
};
const ReadOnlyBar = memo(ReadOnlyBarComp);

const TemporaryViewBar = () => {
  const [loadTime] = useState(Date.now());
  const history = useHistory();
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();

  return (
    <div className="view-bar unsaved-view">
      <span className="view-bar__left" style={{color: '#fff'}}>
        You are viewing a temporary workspace. Changes to this workspace will
        not be saved.
      </span>
      <span className="view-bar__right">
        <Button
          size="tiny"
          content="Return to artifact view"
          style={{background: '#f4f4f4', color: '#363a3d'}}
          onClick={() => {
            navigateFromTempViewBar({
              entityName,
              projectName,
              destination: window.location.pathname + '/artifacts',
              destinationTab: 'Artifacts',
              viewTime: Date.now() - loadTime,
            });
            history.go(-1);
          }}
        />
        <Button
          size="tiny"
          content="Go to my workspace"
          className=""
          style={{background: '#f4f4f4', color: '#363a3d'}}
          onClick={() => {
            navigateFromTempViewBar({
              entityName,
              projectName,
              destination: window.location.pathname,
              destinationTab: 'Workspace',
              viewTime: Date.now() - loadTime,
            });
            history.push(window.location.pathname);
          }}
        />
      </span>
    </div>
  );
};

const ViewBarComp: FC<AllViewBarProps> = ({
  cantRedo,
  cantUndo,
  copyToOwn,
  discardChanges,
  entityName,
  extraActions,
  isRun,
  loading,
  loadOwn,
  projectName,
  readOnly,
  redo,
  undo,
  viewer,
  workspaceID,
  workspaceIsPersisted,
  workspaceType,
  workspaceUpdatedAt,
  workspaceUser,
}: AllViewBarProps) => {
  useTemporaryViewReload();

  const renderAnonymousBar = viewer == null || viewer.id === LOGGED_OUT_USER_ID;
  const renderReadOnlyBar = readOnly && !viewingAs();

  const isOwnView =
    viewer != null &&
    workspaceUser != null &&
    viewer.id === workspaceUser.id &&
    workspaceType === WorkspaceTypes.user;

  const isUnsavedFork = !cantUndo && !isOwnView;

  useEffect(() => {
    if (loading || renderAnonymousBar || renderReadOnlyBar || !isUnsavedFork) {
      return;
    }
    window.analytics?.track('Unsaved fork workspace bar viewed');
  }, [loading, renderAnonymousBar, renderReadOnlyBar, isUnsavedFork]);

  if (loading) {
    return <div />;
  }
  if (renderAnonymousBar) {
    return <div />;
  }
  if (renderReadOnlyBar) {
    return (
      <ReadOnlyBar
        entityName={entityName}
        projectName={projectName}
        cantUndo={cantUndo}
        cantRedo={cantRedo}
      />
    );
  }

  let workspaceName;
  if (workspaceType === WorkspaceTypes.default) {
    workspaceName = 'Default Workspace';
  } else if (workspaceUser != null) {
    if (viewer?.id === workspaceUser.id) {
      workspaceName = 'My Workspace';
    } else {
      workspaceName = workspaceUser.username + "'s workspace";
    }
  } else {
    workspaceName = '';
  }

  let workspaceHelpText = '';
  if (workspaceType === WorkspaceTypes.default) {
    workspaceHelpText +=
      'You are currently viewing the default workspace, load your own workspace to make changes.';
  } else if (!isOwnView) {
    workspaceHelpText +=
      "You are viewing another user's workspace, load your own workspace to make changes.";
  } else {
    workspaceHelpText +=
      'Your workspace auto-saves your configuration of charts, filters, and table settings.';
  }

  if (isOwnView) {
    if (isRun) {
      workspaceHelpText +=
        ' ' +
        `This workspace will be shared across all runs in ${projectName}.`;
    }
  }

  if (urlStateHasKey(URL_STATE_KEYS.TEMPORARY_VIEW)) {
    return <TemporaryViewBar />;
  }

  return (
    <div
      className={`view-bar ${
        isOwnView ? 'own-view' : isUnsavedFork ? 'unsaved-view' : 'others-view'
      }`}>
      <span className="view-bar__left">
        <ViewMenu
          workspaceID={workspaceID}
          isOwnView={isOwnView}
          currentView={
            workspaceType === WorkspaceTypes.default || !workspaceUser
              ? 'default'
              : workspaceUser.username
          }
          trigger={
            <Button
              className="view-menu-button"
              onClick={() =>
                window.analytics?.track('Workspace dropdown clicked')
              }>
              {workspaceType === WorkspaceTypes.default && (
                <S.DefaultHomeIcon>
                  <IconHome />
                </S.DefaultHomeIcon>
              )}
              {!isUnsavedFork &&
                workspaceType !== WorkspaceTypes.default &&
                workspaceUser != null &&
                viewer?.id !== workspaceUser.id && (
                  <Image
                    src={workspaceUser.photoUrl}
                    onError={(img: any) =>
                      (img.target.style.visibility = 'hidden')
                    }
                    avatar
                  />
                )}
              {isUnsavedFork ? 'Unsaved fork of ' : ''}
              {workspaceName}
              <LegacyWBIcon name="overflow" title="menu" />
            </Button>
          }
        />
      </span>
      <span className="view-bar__middle">{extraActions}</span>
      <span className="view-bar__right">
        {isOwnView && (
          <span className="workspace-updated-text hide-in-mobile">
            <Popup
              wide={true}
              position="top center"
              trigger={
                <span className="help-text underline-dashed">
                  {workspaceIsPersisted &&
                    workspaceUpdatedAt &&
                    (cantUndo && cantRedo ? (
                      <>
                        Updated{' '}
                        <TimeAgo
                          minPeriod={10}
                          formatter={(
                            value,
                            unit,
                            suffix,
                            epochSeconds,
                            nextFormatter
                          ) =>
                            unit === 'second'
                              ? 'just now'
                              : nextFormatter!(
                                  value,
                                  unit,
                                  suffix,
                                  epochSeconds
                                )
                          }
                          date={workspaceUpdatedAt + 'Z'}
                        />
                      </>
                    ) : (
                      <>Changes saved automatically</>
                    ))}
                </span>
              }
              content={workspaceHelpText}
            />
          </span>
        )}
        {isOwnView && (
          <Button.Group size="tiny">
            <Button disabled={cantUndo} icon="undo" onClick={undo} />
            <Button disabled={cantRedo} onClick={redo} icon="redo" />
          </Button.Group>
        )}

        {(viewer?.id == null || workspaceUser == null || !isOwnView) &&
          !isUnsavedFork && (
            <Route
              render={({history}) => (
                <Button
                  size="tiny"
                  content="Return to my workspace"
                  className="return-button"
                  onClick={() => {
                    loadOwn();
                    window.analytics?.track('Return to my workspace clicked', {
                      location: 'workspace bar',
                    });
                  }}
                />
              )}
            />
          )}
        {isUnsavedFork && (
          <Button
            size="tiny"
            color="red"
            content="Discard changes"
            className="discard-button"
            onClick={() => {
              discardChanges();
              window.analytics?.track('Unsaved fork workspace discard clicked');
            }}
          />
        )}
        {!isOwnView && (
          <Button
            size="tiny"
            content="Copy to my workspace"
            className="copy-button"
            onClick={() => {
              copyToOwn();
              window.analytics?.track('Copy workspace clicked', {
                location: 'workspace bar',
              });
            }}
          />
        )}
      </span>
    </div>
  );
};
const ViewBarCompMemo = memo(ViewBarComp);

function useViewBarProps(props: ViewBarProps) {
  const viewer = ViewerHooks.useViewer();
  const workspaceView = useSelector(state => {
    const workspace = state.workspaces[props.workspaceID];
    if (workspace == null || workspace.viewRef == null) {
      return undefined;
    }
    return state.views.views[workspace.viewRef.id];
  });
  if (workspaceView == null) {
    throw new Error('invalid state');
  }
  const workspaceType = getWorkspaceType(workspaceView.name);
  const workspaceUser = workspaceView.user;
  const workspaceUpdatedAt = workspaceView.updatedAt;
  const workspaceIsPersisted = workspaceView.id != null;
  const cantUndo = useSelector(state => state.views.undoActions.length === 0);
  const cantRedo = useSelector(state => state.views.redoActions.length === 0);
  const discardChanges = useWorkspaceAction(
    props.workspaceID,
    WorkspaceActions.discardChanges
  );
  const loadOwn = useWorkspaceAction(
    props.workspaceID,
    WorkspaceActions.loadOwn
  );
  const copyToOwn = useWorkspaceAction(
    props.workspaceID,
    WorkspaceActions.copyToOwn
  );
  const dispatch = useDispatch();
  const undo = useCallback(() => dispatch(ViewActions.undo()), [dispatch]);
  const redo = useCallback(() => dispatch(ViewActions.redo()), [dispatch]);

  useUndoHotkeys(undo, redo);

  return {
    viewer,
    loading: workspaceView == null,
    workspaceIsPersisted,
    workspaceUser,
    workspaceType,
    workspaceUpdatedAt,
    cantUndo,
    cantRedo,
    discardChanges,
    loadOwn,
    copyToOwn,
    undo,
    redo,
  };
}

export const ViewBar = (props: ViewBarProps) => {
  const selectedProps = useViewBarProps(props);
  return <ViewBarCompMemo {...props} {...selectedProps} />;
};

type UndoFn = () => void;

// TODO(axel): generalize this hotkey BS
function useUndoHotkeys(undo: UndoFn, redo: UndoFn): void {
  useEffect(() => {
    const modifierKey = isMac ? 'Meta' : 'Control';
    let modifierHeld = false;
    let shiftHeld = false;

    const onKeydown = (e: KeyboardEvent) => {
      if (e.repeat) {
        return;
      }
      switch (e.key) {
        case modifierKey:
          modifierHeld = true;
          return;
        case 'Shift':
          shiftHeld = true;
          return;
        case 'z':
          if (modifierHeld && !isDocumentFocusedOnTextBox()) {
            shiftHeld ? redo() : undo();
            e.preventDefault();
          }
          return;
      }
    };

    const onKeyup = (e: KeyboardEvent) => {
      switch (e.key) {
        case modifierKey:
          modifierHeld = false;
          return;
        case 'Shift':
          shiftHeld = false;
          return;
      }
    };

    window.addEventListener('keydown', onKeydown);
    window.addEventListener('keyup', onKeyup);
    return () => {
      window.removeEventListener('keydown', onKeydown);
      window.removeEventListener('keyup', onKeyup);
    };
  }, [undo, redo]);
}

const TEXT_INPUT_TYPES = [
  'text',
  'password',
  'number',
  'email',
  'tel',
  'url',
  'search',
  'date',
  'datetime',
  'datetime-local',
  'time',
  'month',
  'week',
];

function isDocumentFocusedOnTextBox(): boolean {
  const activeEl = document.activeElement;
  if (activeEl == null || !(activeEl instanceof HTMLElement)) {
    return false;
  }
  return isTextBox(activeEl);
}

function isTextBox(el: HTMLElement): boolean {
  if (el.isContentEditable) {
    return true;
  }

  const tagName = el.tagName.toLowerCase();
  if (tagName === 'textarea') {
    return true;
  }
  if (tagName !== 'input') {
    return false;
  }

  const type = el.getAttribute('type')?.toLowerCase();
  return isOneOf(type, TEXT_INPUT_TYPES);
}
